/* eslint-disable react-refresh/only-export-components */
// context/hook 与组件同文件为有意模式;react-refresh 仅关注 HMR,不影响运行时。
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { AlertTriangle, Info, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";

/**
 * s15.2 (2026-05-08): 替代 window.confirm 的统一 imperative API。
 *
 * 项目跨 platform / tenant / wecom-h5 三套 entry 但共用同一套 shadcn cyber 主题
 * tokens, AlertDialog 自动适配; 旧的 window.confirm 用浏览器原生弹框跟整体视觉
 * 完全脱节。本 hook 提供:
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "删除任务?",
 *     description: "删除任务将级联删除所有成品文件",
 *     confirmText: "删除",
 *     variant: "destructive",
 *   });
 *   if (!ok) return;
 *
 * variant:
 *   - "default": 普通确认 (info 图标)
 *   - "warning": 操作有风险但可逆 (warning 三角)
 *   - "destructive": 不可逆操作 (垃圾桶, 红色确认按钮)
 */
export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: "default" | "warning" | "destructive";
}

interface ConfirmCtxValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const Ctx = createContext<ConfirmCtxValue | null>(null);

const DEFAULT_OPTS: ConfirmOptions = { title: "" };

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOptions>(DEFAULT_OPTS);
  const resolveRef = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback((o: ConfirmOptions) => {
    setOpts(o);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  function close(result: boolean) {
    setOpen(false);
    const r = resolveRef.current;
    resolveRef.current = null;
    // 让退出动画跑完再 resolve 防止 caller 在弹框还没消失时立刻打开下一个
    setTimeout(() => r?.(result), 0);
  }

  const variant = opts.variant ?? "default";
  const Icon = variant === "destructive" ? Trash2 : variant === "warning" ? AlertTriangle : Info;
  const iconClass =
    variant === "destructive"
      ? "text-destructive"
      : variant === "warning"
        ? "text-warning"
        : "text-primary";
  const actionClass =
    variant === "destructive"
      ? "bg-destructive text-destructive-foreground hover:bg-destructive/90 focus:ring-destructive"
      : "";

  return (
    <Ctx.Provider value={{ confirm }}>
      {children}
      <AlertDialog
        open={open}
        onOpenChange={(o) => {
          if (!o) close(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader className="min-h-0">
            <AlertDialogTitle className="flex shrink-0 items-center gap-2">
              <Icon className={`h-5 w-5 shrink-0 ${iconClass}`} />
              <span>{opts.title}</span>
            </AlertDialogTitle>
            {opts.description != null && opts.description !== "" && (
              <AlertDialogDescription className="min-h-0 overflow-y-auto whitespace-pre-wrap">
                {opts.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter className="shrink-0">
            <AlertDialogCancel onClick={() => close(false)}>
              {opts.cancelText ?? t("actions.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => close(true)} className={actionClass}>
              {opts.confirmText ?? t("actions.confirmAlt")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Ctx.Provider>
  );
}

export function useConfirm(): ConfirmCtxValue["confirm"] {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("useConfirm must be used inside <ConfirmProvider>");
  }
  return ctx.confirm;
}
